import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

// Local components and styles
import HeaderItem from '../shared/HeaderItem';
import {
    Wrapper,
    LeftContentWrapper,
    RightContentWrapper,
    MiddleContentWrapper,
    LogoWrapper,
    IconWrapper,
    SearchWrapper,
    SearchForm,
    SearchInput,
    SearchButton,
    SearchSettingsButton,
    SearchSettingsPanel,
    SettingsSection,
    SettingsSectionTitle,
    SettingsCheckboxGroup,
    VersionWrapper,
} from './PageHeader.styles';

// Side panel components and styles
import SidePanelPopup, { SidePanelModulEnum } from '../common/side-panel-popup/SidePanelPopup';
import {
    userInfoPanelClass,
    logoutSectionClass,
    appsContentClass,
    userRolesSectionClass,
    rolesContainerClass,
    roleItemContainerClass,
    roleNameSpanClass,
} from '../common/side-panel-popup/SidePanelPopup.styles';

// Authentication and API hooks
import useMsal from '../../auth/useMsal';
import { useAuthStore } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { useAppsMenuStore } from '../../context/AppsMenuContext';
import { getUniqueRoleDisplayNames } from '../../utils/roleMapper';
import '../../utils/fontAwesomeSetup';

const PageHeader = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showSearchSettings, setShowSearchSettings] = useState(false);
    const [searchOptions, setSearchOptions] = useState({
        searchProductCategoryNames: true,
        searchTradeNames: true,
        searchSynonyms: true,
        searchChartererNames: true,
        searchCasNumber: true,
    });
    const [filterCriteria, setFilterCriteria] = useState({
        marpolRegulationIsAnnexI: false,
        marpolRegulationIsAnnexIIChapter17: false,
        marpolRegulationIsAnnexIIChapter18: false,
        marpolRegulationIsGas: false,
    });
    const [visiblePopup, setVisiblePopup] = useState<SidePanelModulEnum>(SidePanelModulEnum.NONE);
    const searchSettingsRef = useRef<HTMLDivElement>(null);
    const { logoutAzAd } = useMsal();
    const { roles, canEdit, canEditChemGuard } = useAuthStore();
    const { isInEditMode, toggleEditMode } = useEditMode();
    const { appsMenu, isLoading: isAppsMenuLoading } = useAppsMenuStore();
    const navigate = useNavigate();
    const location = useLocation();

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        console.log('Searching for:', searchTerm);
        console.log('Search options:', searchOptions);
        console.log('Filter criteria:', filterCriteria);
        // TODO: Implement product search functionality
    };

    const toggleSearchSettings = () => {
        setShowSearchSettings(!showSearchSettings);
    };

    const toggleAllSearchOptions = () => {
        const allSelected = Object.values(searchOptions).every((v) => v);
        setSearchOptions({
            searchProductCategoryNames: !allSelected,
            searchTradeNames: !allSelected,
            searchSynonyms: !allSelected,
            searchChartererNames: !allSelected,
            searchCasNumber: !allSelected,
        });
    };

    const toggleAllFilterCriteria = () => {
        const allSelected = Object.values(filterCriteria).every((v) => v);
        setFilterCriteria({
            marpolRegulationIsAnnexI: !allSelected,
            marpolRegulationIsAnnexIIChapter17: !allSelected,
            marpolRegulationIsAnnexIIChapter18: !allSelected,
            marpolRegulationIsGas: !allSelected,
        });
    };

    const handleSearchOptionChange = (option: keyof typeof searchOptions) => {
        setSearchOptions((prev) => ({ ...prev, [option]: !prev[option] }));
    };

    const handleFilterCriteriaChange = (criteria: keyof typeof filterCriteria) => {
        setFilterCriteria((prev) => ({ ...prev, [criteria]: !prev[criteria] }));
    };

    // Close settings panel when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchSettingsRef.current && !searchSettingsRef.current.contains(event.target as Node)) {
                setShowSearchSettings(false);
            }
        };

        if (showSearchSettings) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showSearchSettings]);

    const handleAboutClick = () => {
        setVisiblePopup(SidePanelModulEnum.About);
    };

    const handleMyAppsClick = () => {
        setVisiblePopup(SidePanelModulEnum.AppsMenu);
    };

    const handleClosePopup = () => {
        setVisiblePopup(SidePanelModulEnum.NONE);
    };

    const handleLogout = () => {
        logoutAzAd();
    };

    const handleProductsClick = () => {
        navigate('/products');
    };

    const handleChangeLogClick = () => {
        navigate('/changelog');
    };

    const handleToolsClick = () => {
        navigate('/tools');
    };

    const handleAdminClick = () => {
        navigate('/admin');
    };

    const handleEditModeToggle = () => {
        toggleEditMode();
    };

    return (
        <Wrapper>
            <LeftContentWrapper>
                <LogoWrapper>
                    <Link to="/home">
                        <h1>
                            Commodity<span className="sub-header">Book</span>
                        </h1>
                    </Link>
                    <VersionWrapper>v1.0.0</VersionWrapper>
                </LogoWrapper>
            </LeftContentWrapper>

            <MiddleContentWrapper>
                <SearchWrapper ref={searchSettingsRef}>
                    <SearchForm onSubmit={handleSearch}>
                        <SearchInput
                            type="text"
                            placeholder="Search for products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <SearchButton type="submit" title="Search for products" aria-label="Search for products">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                            </svg>
                        </SearchButton>
                        <SearchSettingsButton
                            type="button"
                            title="Search settings"
                            aria-label="Search settings"
                            onClick={toggleSearchSettings}
                            className={showSearchSettings ? 'active' : ''}
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" />
                            </svg>
                        </SearchSettingsButton>
                    </SearchForm>
                    {showSearchSettings && (
                        <SearchSettingsPanel>
                            <SettingsSection>
                                <SettingsSectionTitle>Search In:</SettingsSectionTitle>
                                <SettingsCheckboxGroup>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={Object.values(searchOptions).every((v) => v)}
                                            onChange={toggleAllSearchOptions}
                                        />
                                        (Select All)
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchProductCategoryNames}
                                            onChange={() => handleSearchOptionChange('searchProductCategoryNames')}
                                        />
                                        Product
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchTradeNames}
                                            onChange={() => handleSearchOptionChange('searchTradeNames')}
                                        />
                                        Trade Name
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchSynonyms}
                                            onChange={() => handleSearchOptionChange('searchSynonyms')}
                                        />
                                        Synonyms
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchChartererNames}
                                            onChange={() => handleSearchOptionChange('searchChartererNames')}
                                        />
                                        Charterer
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={searchOptions.searchCasNumber}
                                            onChange={() => handleSearchOptionChange('searchCasNumber')}
                                        />
                                        CAS number
                                    </label>
                                </SettingsCheckboxGroup>
                            </SettingsSection>
                            <SettingsSection>
                                <SettingsSectionTitle>Filter:</SettingsSectionTitle>
                                <SettingsCheckboxGroup>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={Object.values(filterCriteria).every((v) => v)}
                                            onChange={toggleAllFilterCriteria}
                                        />
                                        (Select All)
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsAnnexI}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexI')}
                                        />
                                        Annex I
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsAnnexIIChapter17}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexIIChapter17')}
                                        />
                                        Annex II Chapter 17
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsAnnexIIChapter18}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsAnnexIIChapter18')}
                                        />
                                        Annex II Chapter 18
                                    </label>
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={filterCriteria.marpolRegulationIsGas}
                                            onChange={() => handleFilterCriteriaChange('marpolRegulationIsGas')}
                                        />
                                        Gas
                                    </label>
                                </SettingsCheckboxGroup>
                            </SettingsSection>
                        </SearchSettingsPanel>
                    )}
                </SearchWrapper>
                {/* Edit Mode Button - Only visible for users with edit permissions */}
                {(canEdit || canEditChemGuard) && (
                    <HeaderItem title="Edit" iconName="pencil-alt" isActive={isInEditMode} onItemClick={handleEditModeToggle} />
                )}
            </MiddleContentWrapper>
            <RightContentWrapper>
                <IconWrapper>
                    <HeaderItem title="Products" iconName="box" isActive={location.pathname === '/products'} onItemClick={handleProductsClick} />
                    <HeaderItem title="Tools" iconName="wrench" isActive={location.pathname === '/tools'} onItemClick={handleToolsClick} />
                    <HeaderItem
                        title="Change Log"
                        iconName="history"
                        isActive={location.pathname === '/changelog'}
                        onItemClick={handleChangeLogClick}
                    />
                    <HeaderItem title="Admin" iconName="user-shield" isActive={location.pathname === '/admin'} onItemClick={handleAdminClick} />
                </IconWrapper>
                <IconWrapper>
                    <HeaderItem title="My apps" iconName="th" onItemClick={handleMyAppsClick} />
                    <HeaderItem title="About" iconName="user-circle" onItemClick={handleAboutClick} />
                </IconWrapper>
            </RightContentWrapper>

            <SidePanelPopup
                visibleModule={visiblePopup}
                onClose={handleClosePopup}
                title={
                    visiblePopup === SidePanelModulEnum.About ? 'About' : visiblePopup === SidePanelModulEnum.AppsMenu ? 'My Odfjell Apps' : undefined
                }
            >
                {visiblePopup === SidePanelModulEnum.About && (
                    <div className={userInfoPanelClass}>
                        {/* User roles from AuthContext */}
                        {roles && roles.length > 0 && (
                            <div className={userRolesSectionClass}>
                                <h4>User Roles:</h4>
                                <div className={rolesContainerClass}>
                                    {getUniqueRoleDisplayNames(roles).map((displayName, index) => (
                                        <div key={index} className={roleItemContainerClass}>
                                            <span className={roleNameSpanClass}>{displayName}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Fallback when no roles loaded */}
                        {(!roles || roles.length === 0) && (
                            <div className={userRolesSectionClass}>
                                <h4>User Roles:</h4>
                                <p>No roles assigned</p>
                            </div>
                        )}

                        <div className={logoutSectionClass}>
                            <button className="logout" onClick={handleLogout}>
                                Sign out
                            </button>
                        </div>
                    </div>
                )}

                {visiblePopup === SidePanelModulEnum.AppsMenu && (
                    <>
                        {isAppsMenuLoading ? (
                            <div className={appsContentClass}>
                                <p>Loading apps menu...</p>
                            </div>
                        ) : appsMenu ? (
                            <div className={appsContentClass} dangerouslySetInnerHTML={{ __html: appsMenu.html }} />
                        ) : (
                            <div className={appsContentClass}>
                                <p>Failed to load apps menu. Please try again later.</p>
                            </div>
                        )}
                    </>
                )}
            </SidePanelPopup>
        </Wrapper>
    );
};

export default PageHeader;
